const httpLib = require('./http-library.js');
const publicConfig = require('../public-config.js');
const { getWebEnv } = require('./api-env');
const host = publicConfig.external_address;

// public

const Payments = {

  /**
   * GET Booking Client Action (SCA - Strong Customer Authentication) Data
   *
   * @function
   * @name getBookingClientActionData
   * @param {String} - Booking ID
   * @param {Function} - Callback
   */

  getBookingClientActionData: (bookingId, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/bookings/${bookingId}/payments/client_action_data`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, cb, null);
  },

  /**
   * @function
   * @name setupIntent
   * @param {String} - ssoID
   * @param {String} - accessToken
   * @param {Object} - payload
   * @param {Function} - Callback
   */

  setupIntent: (ssoId, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoId}/intent`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, cb, payload);
  },

  /**
   * @function
   * @name createSetupIntent
   * @param {String} - ssoID
   * @param {String} - accessToken
   * @param {Object} - payload
   * @param {Function} - Callback
   */

  createSetupIntent: (ssoId, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoId}/intent/create`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, cb, payload);
  },

  /**
   * @function
   * @name confirmIntent
   * @param {String} - ssoID
   * @param {String} - paymentIntentId
   * @param {String} - accessToken
   * @param {Object} - payload
   * @param {Function} - Callback
   */

  confirmIntent: (ssoId, paymentIntentId, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoId}/intent/${paymentIntentId}/confirm`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };
    httpLib.request(requestParams, cb, payload);
  },

  /**
   * GET Booking Payment Data
   *
   * @function
   * @name getBookingPaymentData
   * @param {String} - Booking ID
   * @param {Function} - Callback
   */

  getBookingPaymentData: (bookingId, accessToken, ssoId, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments?booking_id=${bookingId}&type=payment${ssoId ? `&sso_id=${ssoId}` : ''}`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Managed Account Stripe Data
   *
   * @function
   * @name getManagedAccountData
   * @param {Function} - Callback
   */

  getManagedAccountData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  /**
   * POST / PUT Managed Account Stripe Data
   *
   * @function
   * @name saveManagedAccountData
   * @param {Function} - Callback
   */

  saveManagedAccountData: (ssoid, accessToken, payload, isUpdate, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts`,
      method: isUpdate ? 'PUT' : 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), payload);
  },

  /**
   * POST Migrate Stripe Account to Managed Account
   *
   * @function
   * @name migrateRecipientAccount
   */

  migrateRecipientAccount: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts/migrate`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res),
      { tos_accepted: true }
    );
  },

  /**
   * GET Recipient Stripe Data
   *
   * @function
   * @name getRecipientData
   * @param {Function} - Callback
   */

  getRecipientData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/recipients`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Customer Stripe Data
   *
   * @function
   * @name getCustomerData
   * @param {Function} - Callback
   */

  getCustomerData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/cards`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  getPaymentsData: (ssoid, paymentId, listingCountry, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/cards/${paymentId}/country/${listingCountry}`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Create a Stripe Token with Form ID
   *
   * @function
   * @name createStripeToken (requires Stripe.js to be loaded)
   * @param {String} - Form Element ID
   * @param {String} - tokenType is either bank-account or card
   * @param {Function} - Callback to recieve response from Stripe library
   */

  createStripeToken: (formElementID, tokenType, cb) => {
    const formElement = document.getElementById(formElementID);
    if ('bank-account' === tokenType) {
      Stripe.bankAccount.createToken(formElement, cb);
    } else if ('card' === tokenType) {
      Stripe.card.createToken(formElement, cb);
    }
  },

  /**
   * POST Bank Data
   *
   * @function
   * @name updateBank
   * @param {String} - ssoid
   * @param {String} - accessToken
   * @param {String} - bankToken created from Stripe.js library
   * @param {Object} - payload object takes 'bank_account_token', 'legal_name', & 'type'
   * @param {Boolean} - isVerify is a flag to say whether or not this is an update to an existing bank account's tax info
   * @param {Function} - Callback
   */

  updateBank: (ssoid, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/recipients`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), payload);
  },

  /**
   * GET Bank Status Data
   *
   * @function
   * @name getBankStatusData
   * @param {Function} - Callback
   */

  getBankStatusData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/bank/ready`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Card Data
   *
   * @function
   * @name addCard
   * @param {Function} - Callback
   */

  addCard: (locale, ssoid, accessToken, cardToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/cards/${cardToken}`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(
      requestParams,
      (res) => {
        if (200 !== res.statusCode || !res.client_action_required) {
          cb(res);
          return;
        }

        getWebEnv(locale, (webEnv) => {
          if (webEnv.error) {
            cb({ error: webEnv.error });
            return;
          }

          const stripeV3 = Stripe.StripeV3(webEnv.stripe.stripe_public_key);
          const paymentMethodId = res.id;
          stripeV3
            .confirmCardSetup(
              res.client_action_client_secret,
              { payment_method: paymentMethodId }
            )
            .then((result) => {
              if (result.error) {
                cb({ error: { errorCode: 'UNABLE_TO_AUTHENTICATE_CARD' } });
                return;
              }
              cb(res);
            });
        });
      },
      null);
  },

  /**
   * POST Upload ID
   *
   * @function
   * @name uploadId
   * @param {Function} - Callback
   */

  uploadId: (data, stripePublicKey, stripeAccountId, ssoId, token, cb) => {
    $.ajax({
      url: 'https://uploads.stripe.com/v1/files',
      data,
      headers: {
        Authorization: 'Bearer ' + stripePublicKey,
        'Stripe-Account': stripeAccountId
      },
      cache: false,
      contentType: false,
      processData: false,
      type: 'POST'
    }).done((respdata) => {
      const payload = {
        individual: {
          verification: {
            document: {
              front: respdata.id
            }
          }
        }
      };

      Payments.saveManagedAccountData(ssoId, token, payload, true, (response) => {
        if (200 !== response.statusCode) {
          return cb(true);
        }

        return cb(false, response);
      });
    }).fail(() => {
      return cb(true);
    });
  },


  /**
   * POST Create Identity Verification Session
   *
   * @function
   * @name createIdentitySession
   * @param {String} - ssoid
   * @param {Function} - Callback
   */

  createIdentitySession: (ssoid, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/verification`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), payload);
  },

  /**
   * GET Customer Identity Verification Session
   *
   * @function
   * @name getCustomerData
   * @param {Function} - Callback
   */

  getIdentitySession: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/verification`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(requestParams, (res) => cb(res), null);
  },

  updatePaymentMethod: (payload, controlId, accessToken, callback) => {
    const params = {
      host,
      method: 'PUT',
      path: `/v2/payments/bookings/${controlId}/payments/scheduled/card`,
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    httpLib.request(
      params,
      (res) => {
        callback(res);
      },
      payload
    );
  }

};

module.exports = Payments;
